<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <div class="row">
      <div class="col">
        <card class="no-scrollbar" style="overflow-x: scroll">
          <div class="row mb-3">
            <div class="col">
              <div
                class="btn-group btn-group-toggle"
                :class="'float-left'"
                data-toggle="buttons"
              >
                <label
                  v-for="(option, index) in categories"
                  :key="index + j"
                  class="btn btn-sm btn-primary btn-simple"
                  :class="{ active: option.active }"
                  :id="index"
                >
                  <input
                    type="radio"
                    @click="switchTab(index)"
                    name="options"
                    autocomplete="off"
                    :checked="option.active"
                  />
                  <span style="font-size: medium; font-weight: lighter"  class="d-none d-sm-block">{{ option.name }}</span>
                  <span class="d-block d-sm-none">
                    <i :class="option.icon"></i>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <filter-bar
            :filters="filterSchema"
            :initial-value="filterValues"
            :result-count="totalRows"
            :per-page="perPage"
            :per-page-options="perPageOptions"
            saved-filters-key="booking-tickets"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
          >
            <template #actions>
              <base-button icon class="btn-link text-white" v-on:click="downloadCSV()">
                <i class="tim-icons icon-cloud-download-93"></i>
              </base-button>
            </template>
          </filter-bar>

          <div class="mt-3" v-if="tickets.length <= 0">
            <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <h4 class="mt-3">No tickets have been sold.</h4>
            </div>
          </div>

          <div class="row d-none d-sm-block" v-if="tickets.length > 0">
            <div class="col">
              <b-table  striped hover :items="tickets"
                        :fields="['event', 'booking_id', 'booking_time', 'ticket_type', 'quantity', 'price', 'customer', 'transaction_id', 'date_purchased']">
                <template #head(booking_time)="data">
                  <div style="white-space: nowrap">booking time</div>
                </template>
                <template #head(ticket_type)="data">
                  <div style="white-space: nowrap">ticket type</div>
                </template>
                <template #head(booking_id)="data">
                  <div style="white-space: nowrap">booking id</div>
                </template>
                <template #head(transaction_id)="data">
                  <div style="white-space: nowrap">transaction id</div>
                </template>
                <template #head(date_purchased)="data">
                  <div style="white-space: nowrap">date purchased</div>
                </template>

                <template v-slot:cell(event)="row">
                  <div style="white-space: nowrap;">
                    {{row.item.game_name}}
                  </div>
                </template>
                <template v-slot:cell(booking_time)="row">
                  <div style="white-space: nowrap;">
                    {{getDate(row.item.booking_date, date_format)}}, {{getTime(row.item.start_time, time_format)}}
                  </div>
                </template>
                <template v-slot:cell(booking_id)="row">
                  <span style="white-space: nowrap;">#{{getBookingId(row.item) }}
                    <el-tooltip content="View booking"
                                effect="light"
                                :open-delay="300"
                                placement="top">
                      <a v-if="hasPermissions('bookings_view') && activeTabIndex === 0" :href="'/bookings?selected_booking=' + getBookingId(row.item)" target="_blank">
                        <i class="tim-icons el-icon-top-right"></i>
                      </a>
                    </el-tooltip>
                  </span>
                </template>
                <template v-slot:cell(transaction_id)="row">
                  <span style="white-space: nowrap;">#{{row.item.order_number}}
                    <el-tooltip content="View transaction"
                                effect="light"
                                :open-delay="300"
                                placement="top">
                      <a v-if="hasPermissions('transactions_view')" :href="'/purchases/transactions/details?id=' + row.item.transaction_id" target="_blank">
                        <i class="tim-icons el-icon-top-right"></i>
                      </a>
                    </el-tooltip>
                  </span>
                </template>
                <template v-slot:cell(ticket_type)="row">
                  <span>{{row.item.category}}</span>
                </template>
                <template v-slot:cell(quantity)="row">
                  <span>{{row.item.number_of_players}}</span>
                </template>
                <template v-slot:cell(price)="row">
                  <div style="white-space: nowrap;">{{getAmount(row.item.price)}}</div>
                  <small style="font-size: 10px; white-space: nowrap">{{getAmount(row.item.rate)}} per ticket</small>
                </template>
                <template v-slot:cell(customer)="row">
                  <div style="white-space: nowrap;" v-if="row.item.customer_id">
                    <div>{{row.item.first_name}}  {{row.item.last_name}}
                      <el-tooltip content="View customer"
                                  effect="light"
                                  :open-delay="300"
                                  placement="top">
                        <a v-if="hasPermissions('customer_view')" :href="'/customers/details?id=' + row.item.customer_id" target="_blank">
                          <i class="tim-icons el-icon-top-right"></i>
                        </a>
                      </el-tooltip>
                    </div>
                    <small class="text-white">
                      <i class="tim-icons icon-email-85"></i> {{row.item.email}}
                    </small>
                    <small class="text-white ml-2" v-if="row.item.phone">
                      <i class="tim-icons el-icon-mobile-phone"></i> {{ formatPhone(row.item.country, row.item.phone) }}
                    </small>
                  </div>
                  <span v-else>-</span>
                </template>
                <template v-slot:cell(date_purchased)="row">
                  <span style="white-space: nowrap;">{{getLocalDateTime(row.item.purchase_date, date_format, time_format)}}</span>
                </template>
                <template v-slot:cell(actions)="row">
                  <el-dropdown>
                    <base-button
                      simple
                      type="primary"
                      link
                    > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                    </base-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <a class="dropdown-item" target="_blank" :href="'/purchases/transactions/details?id=' + row.item.transaction_id">
                          <i class="tim-icons text-info icon-notes mr-2"></i> View transaction
                        </a>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </template>
              </b-table>
            </div>
          </div>

          <div class="row d-block d-sm-none" v-if="tickets.length > 0">
            <div class="col">
              <b-table  striped hover :items="tickets"
                        :fields="['tickets']">
                <template #head(tickets)="data">
                  <div style="white-space: nowrap">Total results: {{totalRows}}</div>
                </template>

                <template v-slot:cell(tickets)="row">
                  <div class="row">
                    <div class="col">
                      <h4>{{row.item.game_name}}</h4>
                    </div>
                    <div class="col-3 text-right">
                      <a v-if="hasPermissions('bookings_view') && activeTabIndex === 0" :href="'/bookings?selected_booking=' + getBookingId(row.item)" target="_blank">
                        <badge type="default">
                          #{{getBookingId(row.item)}}
                        </badge>
                      </a>
                    </div>
                  </div>
                  <div style="white-space: nowrap;">
                    Booking time: {{getDate(row.item.booking_date, date_format)}}, {{getTime(row.item.start_time, time_format)}}
                  </div>
                  <div>
                    Ticket type: <badge class="ml-1" type="default">{{row.item.category}}</badge>
                  </div>
                  <div>
                    Quantity: {{row.item.number_of_players}}
                  </div>
                  <div>
                    Price: {{getAmount(row.item.price)}} ({{getAmount(row.item.rate)}} per ticket)
                  </div>
                  <div>
                    Transaction #{{row.item.order_number}}
                    <el-tooltip content="View transaction"
                                effect="light"
                                :open-delay="300"
                                placement="top">
                      <a v-if="hasPermissions('transactions_view')" :href="'/purchases/transactions/details?id=' + row.item.transaction_id" target="_blank">
                        <i class="tim-icons el-icon-top-right"></i>
                      </a>
                    </el-tooltip>
                  </div>
                  <div>
                    Customer: {{row.item.first_name}}  {{row.item.last_name}}
                    <el-tooltip content="View customer"
                                effect="light"
                                :open-delay="300"
                                placement="top">
                      <a v-if="hasPermissions('customer_view')" :href="'/customers/details?id=' + row.item.customer_id" target="_blank">
                        <i class="tim-icons el-icon-top-right"></i>
                      </a>
                    </el-tooltip>
                  </div>
                  <div>
                    <small class="text-white">
                      <i class="tim-icons icon-email-85"></i> {{row.item.email}}
                    </small>
                    <small class="text-white ml-2" v-if="row.item.phone">
                      <i class="tim-icons el-icon-mobile-phone"></i> {{ formatPhone(row.item.country, row.item.phone) }}
                    </small>
                  </div>
                  <div>
                    Date purchased: {{getLocalDateTime(row.item.purchase_date, date_format, time_format)}}
                  </div>
                </template>
              </b-table>
            </div>
          </div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            v-on:input="getTickets(perPage, getOffset())"
          ></b-pagination>
        </card>
      </div>
    </div>
  </div>
</template>

<script>
import currencies from "@/assets/currencies.json";
import {API_LOCATION} from "@/api-config";
import swal from "sweetalert2";
import {Auth} from "aws-amplify";
import {BFormInput, BIcon, BIconArrowCounterclockwise, BIconFilter, BPagination, BTable} from "bootstrap-vue";
import {Badge, BaseAlert} from "@/components";
import {Dropdown, DropdownItem, DropdownMenu} from "element-ui";
import _ from "lodash";
import {localDateTime, date, time, formatGroupDate, formatGroupTime} from "@/plugins/dates";
import axios from "axios";
import {formatPhoneDisplay} from "@/util/phoneFormatter";

export default {
  name: "TicketList",
  components: {
    BFormInput,
    BTable,
    Badge,
    BPagination,
    BIcon,
    BIconFilter,
    BIconArrowCounterclockwise,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    BaseAlert
  },
  data() {
    return {
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      first_day_of_week: JSON.parse(localStorage.getItem('group')).first_day_of_week,
      currencies: currencies.currencies,
      number_format: JSON.parse(localStorage.getItem('group')).number_format,
      currency_symbol: '$',
      currency: JSON.parse(localStorage.getItem('group')).currency,
      currency_format: JSON.parse(localStorage.getItem('group')).currency_format,
      perPageOptions: [5, 10, 25, 50],
      loading: false,
      tickets: [],
      games: [],
      ticket_types: [],
      currentPage: 1,
      totalRows: 0,
      perPage: 10,
      activeTabIndex: 0,
      j: 0,
      // FilterBar (saved-views enabled) drives the first fetch via its initial
      // `change`; this guards the page number on that first call.
      didInitialFetch: false,
      // Flat filter values for the shared <filter-bar>. Seeded from the URL in
      // mounted; saved-views precedence is resolved by FilterBar. event = game ids
      // (multiselect), type = ticket-type categories (multiselect).
      filterValues: {
        event: [],
        booking: '',
        booking_date: { mode: 'range', days: '', from: '', to: '' },
        type: [],
        quantity: { from: '', to: '' },
        price: { from: '', to: '' },
        customer: '',
        transaction: '',
        purchased: { mode: 'range', days: '', from: '', to: '' },
      },
    }
  },
  computed:{
    categories() {
      return [{ name: 'Tickets', icon: 'tim-icons icon-bullet-list-67', active: true },
        { name: 'Cancelled tickets', icon: 'tim-icons icon-chart-bar-32', active: false }
      ];
    },
    // Schema for the shared <filter-bar>. event/type are multiselect; the two date
    // filters are dateranges; quantity/price are number ranges.
    filterSchema() {
      return [
        { key: 'event', type: 'multiselect', label: 'Event', primary: true,
          options: (Array.isArray(this.games) ? this.games : []).map(g => ({ label: g.name, value: g.id })) },
        { key: 'booking', type: 'text', label: 'Booking id', placeholder: 'Booking id', operatorLabel: 'is', primary: true },
        { key: 'booking_date', type: 'daterange', label: 'Booking date', primary: true },
        { key: 'type', type: 'multiselect', label: 'Ticket type', primary: true,
          options: (Array.isArray(this.ticket_types) ? this.ticket_types : []).map(t => ({ label: t.category, value: t.category })) },
        { key: 'quantity', type: 'numberrange', label: 'Quantity' },
        { key: 'price', type: 'numberrange', label: 'Price' },
        { key: 'customer', type: 'text', label: 'Customer', placeholder: 'Customer', operatorLabel: 'contains' },
        { key: 'transaction', type: 'text', label: 'Transaction id', placeholder: 'Transaction id', operatorLabel: 'is' },
        { key: 'purchased', type: 'daterange', label: 'Purchased' },
      ]
    }
  },
  methods: {
    formatPhone(country, phone) {
      return formatPhoneDisplay(phone, country || 'US');
    },
    switchTab(index){
      this.activeTabIndex = index
      for(let n in this.categories){
        this.categories[n].active = false
      }
      this.categories[index].active = true
      this.j++
      // Switching datasets lands on page 1 so the offset never runs past the new set.
      this.currentPage = 1
      this.getTickets(this.perPage, this.getOffset())
    },
    getBookingId(item){
      return item.master_slot_id ? item.master_slot_id : item.booking_slot_id
    },
    // FilterBar change: apply new values and (re)fetch. The first change is
    // FilterBar's initial saved-views resolution and drives the first load.
    onFiltersChanged(values){
      this.filterValues = {
        event: Array.isArray(values.event) ? values.event : [],
        booking: values.booking || '',
        booking_date: { ...(values.booking_date || { mode: 'range', days: '', from: '', to: '' }) },
        type: Array.isArray(values.type) ? values.type : [],
        quantity: { ...(values.quantity || { from: '', to: '' }) },
        price: { ...(values.price || { from: '', to: '' }) },
        customer: values.customer || '',
        transaction: values.transaction || '',
        purchased: { ...(values.purchased || { mode: 'range', days: '', from: '', to: '' }) },
      }
      if(!this.didInitialFetch){
        this.didInitialFetch = true
        this.getTickets(this.perPage, this.getOffset())
      } else {
        // Applying a filter/view lands on page 1; reset BEFORE building the offset
        // (a stale page would produce an out-of-range offset that 502s the backend).
        this.currentPage = 1
        this.getTickets(this.perPage, this.getOffset())
      }
    },
    onPerPageChange(n){
      this.perPage = n
      this.currentPage = 1
      this.getTickets(this.perPage, this.getOffset())
    },
    // Derive raw yyyy-MM-dd from/to from a daterange filter value, preserving the
    // exact param shape the backend already expects (booking_from/to, purchased_from/to).
    dateRangeParams(d){
      d = d || {}
      if(d.mode === 'days' && d.days !== '' && d.days != null){
        const today = new Date()
        const dt = new Date()
        dt.setDate(dt.getDate() - Number(d.days))
        const from = dt.getFullYear() + '-' + ('0' + (dt.getMonth() + 1)).slice(-2) + '-' + ('0' + dt.getDate()).slice(-2)
        const to = today.getFullYear() + '-' + ('0' + (today.getMonth() + 1)).slice(-2) + '-' + ('0' + today.getDate()).slice(-2)
        return { from, to }
      }
      if(d.mode === 'range' && d.from && d.to){
        return { from: d.from, to: d.to }
      }
      return { from: '', to: '' }
    },
    escapeCSVValue(value) {
      if (typeof value === 'string') {
        // Escape double quotes by doubling them
        value = value.replace(/"/g, '""');

        // If the value contains a comma, wrap it in double quotes
        if (value.includes(',') || value.includes('\n')) {
          value = `"${value}"`;
        }
      }
      return value;
    },
    async downloadCSV(){
      //get the entire data set
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      this.loading = true
      let count = 5000
      const csvData = await this.getAllTickets(config, count)
      this.loading = false

      let csv = 'Event, Booking Id, Booking Time, Ticket Type, Quantity, Price, Customer Name, Customer Email, Customer Phone, Transaction Id, Date Purchased\n';
      csvData.forEach(row => {
        csv += [this.escapeCSVValue(row.game_name),
          this.escapeCSVValue(this.getBookingId(row)),
          this.escapeCSVValue(this.getDate(row.booking_date, this.date_format) + ', ' + this.getTime(row.start_time, this.time_format)),
          this.escapeCSVValue(row.category),
          this.escapeCSVValue(row.number_of_players),
          this.escapeCSVValue(row.price * row.number_of_players),
          this.escapeCSVValue(row.first_name + ' ' + row.last_name),
          this.escapeCSVValue(row.email),
          this.escapeCSVValue(row.phone),
          this.escapeCSVValue(row.order_number),
          this.escapeCSVValue(this.getLocalDateTime(row.purchase_date, this.date_format, this.time_format))]
        csv += "\n";
      });

      const anchor = document.createElement('a');
      anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      anchor.target = '_blank';
      anchor.download = 'tickets.csv';
      anchor.click();
    },
    async getAllTickets(config, count){
      let url = ''
      let promises = []
      let csvData = []
      let offset = 0
      const f = this.filterValues
      const event = (f.event || []).join(',')
      const type = (f.type || []).join(',')
      const bd = this.dateRangeParams(f.booking_date)
      const pd = this.dateRangeParams(f.purchased)
      const price = f.price || {}
      const quantity = f.quantity || {}
      while(offset <= this.totalRows){

        url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/tickets?count=' + count
          + "&offset=" + offset
          + "&event=" + encodeURIComponent(event)
          + "&type=" + encodeURIComponent(type)
          + "&booking=" + encodeURIComponent(f.booking)
          + "&transaction=" + encodeURIComponent(f.transaction)
          + "&customer=" + encodeURIComponent(f.customer)
          +"&price_to=" + encodeURIComponent(price.to)
          +"&price_from=" + encodeURIComponent(price.from)
          +"&quantity_to=" + encodeURIComponent(quantity.to)
          +"&quantity_from=" + encodeURIComponent(quantity.from)
          +"&purchased_to=" + encodeURIComponent(pd.to)
          +"&purchased_from=" + encodeURIComponent(pd.from)
          +"&booking_to=" + encodeURIComponent(bd.to)
          +"&booking_from=" + encodeURIComponent(bd.from)
          +"&tab=" + encodeURIComponent(this.activeTabIndex)

        promises.push(
          axios.get(url, config)
            .then(response =>{
              csvData.push(response.data.tickets)
            })
            .catch(err => {
                console.log(err)
                if (err.response.status == 401) {
                  swal({
                    title: 'Error',
                    html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                    type: 'error',
                  }).then((response)=> {
                    this.$router.push('/')
                  })
                } else {
                  swal('Error!', 'Something went wrong', 'error')
                }
              }
            )
        )
        offset = offset + Number(count)
      }

      await Promise.all(promises)

      // merge all response arrays into one
      let arr = []
      csvData.forEach((element) => {
        arr = arr.concat(element)

      })
      return arr
    },
    getTime(d, time_format){
      return time(d, time_format)
    },
    getDate(d, date_format){
      return date(d, date_format)
    },
    getLocalDateTime(d){
      if(!d) return ''
      const dt = formatGroupDate(d, { dayOfWeek: false })
      const tm = formatGroupTime(d)
      if(!dt && !tm) return ''
      return `${dt}, ${tm}`
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    getAmount(integer) {
      if (integer == null) {
        return;
      }

      // Get currency info
      const currencyInfo = _.find(this.currencies, { code: this.currency });
      const symbol = currencyInfo ? currencyInfo.symbol : this.currency_symbol;
      const isPostfix = currencyInfo ? currencyInfo.postfix : false;

      let formattedAmount;

      if (this.number_format === 1) {
        // European formatting (comma as decimal separator)
        formattedAmount = integer.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
      } else {
        // Standard formatting (dot as decimal separator)
        formattedAmount = parseFloat(integer).toFixed(2);
      }

      if (this.currency_format === 0) {
        // Symbol format
        if (isPostfix) {
          return formattedAmount + symbol;  // 12.34kr
        } else {
          return symbol + formattedAmount;  // $12.34
        }
      } else {
        // Currency code format
        return formattedAmount + ' ' + this.currency;  // 12.34 SEK
      }
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async getTickets(count, offset){
      const f = this.filterValues
      const event = (f.event || []).join(',')
      const type = (f.type || []).join(',')
      const bd = this.dateRangeParams(f.booking_date)
      const pd = this.dateRangeParams(f.purchased)
      const price = f.price || {}
      const quantity = f.quantity || {}

      this.loading = true
      const token = await this.getAuthToken()
      const config = {headers: {Authorization: token}}
      const url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId  +
        '/tickets?count=' + count + "&offset=" + offset + "&event=" + encodeURIComponent(event)
        + "&type=" + encodeURIComponent(type)
        + "&booking=" + encodeURIComponent(f.booking)
        + "&transaction=" + encodeURIComponent(f.transaction)
        + "&customer=" + encodeURIComponent(f.customer)
        +"&price_to=" + encodeURIComponent(price.to)
        +"&price_from=" + encodeURIComponent(price.from)
        +"&quantity_to=" + encodeURIComponent(quantity.to)
        +"&quantity_from=" + encodeURIComponent(quantity.from)
        +"&purchased_to=" + encodeURIComponent(pd.to)
        +"&purchased_from=" + encodeURIComponent(pd.from)
        +"&booking_to=" + encodeURIComponent(bd.to)
        +"&booking_from=" + encodeURIComponent(bd.from)
        +"&tab=" + encodeURIComponent(this.activeTabIndex)
      this.axios.get(url, config)
        .then(response => {
          this.tickets = response.data.tickets
          this.ticket_types = response.data.ticket_types
          this.totalRows = response.data.total_rows
          this.addQueryParameters()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.error(err)
          if(err.response.status == 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    async getGames(){
      const token = await this.getAuthToken()
      const config = { headers: { Authorization: token } }
      const url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/games?status=all'
      axios.get(url, config)
        .then(response => {
          this.games = response.data
        })
        .catch(err => {
          console.log(err)
          if(err.response.status == 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    addQueryParameters() {
      this.$nextTick(() => {
        this.currentPage = this.$route.query.page
      });

      const f = this.filterValues
      const bd = this.dateRangeParams(f.booking_date)
      const pd = this.dateRangeParams(f.purchased)
      const price = f.price || {}
      const quantity = f.quantity || {}
      const newQuery = {
        ...this.$route.query,
        count: this.perPage,
        page: this.currentPage,
        booking: f.booking,
        type: (f.type || []).join(','),
        transaction: f.transaction,
        customer: f.customer,
        price_to: price.to,
        price_from: price.from,
        quantity_to: quantity.to,
        quantity_from: quantity.from,
        purchased_to: pd.to,
        purchased_from: pd.from,
        booking_to: bd.to,
        booking_from: bd.from,
        event: (f.event || []).join(','),
      }
      this.$router.replace({query: newQuery}).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
    getOffset(){
      // Clamp to >= 0 so a stale/NaN page never yields a negative offset (502s the backend).
      return Math.max(0, Number((this.currentPage * this.perPage) - this.perPage) || 0)
    },
  },
  mounted(){
    this.currency_symbol =  _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol
    this.perPage = Number(this.$route.query.count) || 10
    this.currentPage = Number(this.$route.query.page) || 1
    this.loading = true
    this.getGames()
    // Seed FilterBar's flat values from the URL. FilterBar resolves saved-views
    // precedence (?view= > URL > default > last-used) and drives the first fetch
    // via its initial `change` (onFiltersChanged) — no self-fetch here.
    const q = this.$route.query || {}
    this.filterValues = {
      event: q.event ? q.event.split(',').map(v => Number(v)) : [],
      booking: q.booking || '',
      booking_date: { mode: 'range', days: '', from: q.booking_from || '', to: q.booking_to || '' },
      type: q.type ? q.type.split(',') : [],
      quantity: { from: q.quantity_from || '', to: q.quantity_to || '' },
      price: { from: q.price_from || '', to: q.price_to || '' },
      customer: q.customer || '',
      transaction: q.transaction || '',
      purchased: { mode: 'range', days: '', from: q.purchased_from || '', to: q.purchased_to || '' },
    }
  }
}
</script>

<style scoped>

</style>
